import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAIAssistant } from "./AIAssistantProvider";
import AIChatHeader from "./AIChatHeader";
import AIChatMessages from "./AIChatMessages";
import AIChatInput from "./AIChatInput";
import AIQuickActions from "./AIQuickActions";
import AIErrorMessage from "./AIErrorMessage";
import QuizConfirmationCard from "./quiz/QuizConfirmationCard";
import QuizSuccessCard from "./quiz/QuizSuccessCard";
import { AI_PANEL_WIDTH_CSS } from "./constants";

const PANEL_BOTTOM_GAP = 24; // px — keeps input clearly above the screen edge

export default function AIAssistantPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    isOpen,
    close,
    messages,
    draft,
    setDraft,
    sendMessage,
    retryMessage,
    sending,
    restoring,
    error,
    clearError,
    routeContext,
    startNewConversation,
    copilotUi,
    confirmCreateQuiz,
    editQuizConfiguration,
    openCreatedQuiz,
    openCreatedQuizResults,
  } = useAIAssistant();

  const handleSend = () => {
    const text = String(draft || "").trim();
    if (!text) return;
    sendMessage(text);
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const handleStarterSelect = (action) => {
    if (!action) return;
    sendMessage(action.prompt || action.label);
  };

  const dynamicOptions = (copilotUi?.optionChips || []).map((opt) => ({
    id: opt.id,
    label: opt.label,
    prompt:
      opt.prompt ||
      (opt.id === "open_results" || opt.id === "open-results"
        ? "__open_results__"
        : opt.id === "start_quiz" || opt.id === "create-quiz"
          ? "Create a quiz"
          : opt.id === "my_results" ||
              opt.id === "check-quiz" ||
              opt.id === "refresh_results" ||
              opt.id === "retry-results"
            ? "Analyze my student quiz results again"
            : opt.id === "ask-quizzes"
              ? "Help me with my quizzes and question bank"
              : opt.id === "use_microlab" || opt.id === "bank:private"
                ? opt.id === "bank:private"
                  ? "Use my private question bank"
                  : "Use Microlab public bank"
                : opt.id?.startsWith("cat:") ||
                    opt.id?.startsWith("topic:") ||
                    opt.id?.startsWith("use_count:")
                  ? opt.id
                  : opt.label),
  }));

  const panelHeight = `calc(100vh - ${PANEL_BOTTOM_GAP}px)`;
  const panelHeightDvh = `calc(100dvh - ${PANEL_BOTTOM_GAP}px)`;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      variant={isMobile ? "temporary" : "persistent"}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isOpen && !isMobile ? AI_PANEL_WIDTH_CSS : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : AI_PANEL_WIDTH_CSS,
          maxWidth: isMobile ? "100%" : 480,
          boxSizing: "border-box",
          borderLeft: "1px solid rgba(148,163,184,0.28)",
          boxShadow: isOpen ? "-12px 0 40px rgba(15, 23, 42, 0.08)" : "none",
          position: "fixed",
          top: 0,
          right: 0,
          // Leave a clear gap above the bottom of the screen.
          bottom: PANEL_BOTTOM_GAP,
          height: panelHeight,
          maxHeight: panelHeight,
          "@supports (height: 100dvh)": {
            height: panelHeightDvh,
            maxHeight: panelHeightDvh,
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: isMobile ? 0 : 16,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <AIChatHeader
            sectionLabel={routeContext.label}
            onClose={close}
            onNewConversation={startNewConversation}
            disabled={sending || restoring}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <AIChatMessages
            messages={messages}
            sending={sending}
            restoring={restoring}
            sectionLabel={routeContext.label}
            onRetry={retryMessage}
            onStarterSelect={handleStarterSelect}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#ffffff",
            borderTop: "1px solid rgba(148,163,184,0.18)",
            pb: 1.5,
          }}
        >
          <AIErrorMessage message={error} onDismiss={clearError} />
          {copilotUi?.createdQuiz ? (
            <QuizSuccessCard
              quiz={copilotUi.createdQuiz}
              onOpenDetails={openCreatedQuiz}
              onOpenResults={openCreatedQuizResults}
            />
          ) : null}
          {copilotUi?.confirmation ? (
            <QuizConfirmationCard
              preview={copilotUi.confirmation}
              onConfirm={confirmCreateQuiz}
              onEdit={editQuizConfiguration}
              confirming={copilotUi.confirming || sending}
              disabled={sending || restoring}
            />
          ) : null}
          <AIQuickActions
            visible={dynamicOptions.length > 0 && !copilotUi?.confirmation}
            disabled={sending || restoring || copilotUi?.confirming}
            onSelect={handleQuickAction}
            actions={dynamicOptions}
          />
          <AIChatInput
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            sending={sending}
            disabled={restoring}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
